// Mes Commandes Marketplace
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Truck, CheckCircle, Clock, AlertTriangle, Search,
  ChevronRight, Eye, MessageCircle, Star, RotateCcw, ArrowLeft, X,
  User, Calendar, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { TicketModal } from '../../../components/TicketModal';

function CommandeCard({ commande, onClick }) {
  const statusConfig = {
    delivered: { label: 'Livré', bg: 'bg-[#1E3A5F]/10', text: 'text-[#1E3A5F]', icon: CheckCircle },
    shipping: { label: 'En livraison', bg: 'bg-blue-50', text: 'text-blue-700', icon: Truck },
    processing: { label: 'En préparation', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
    problem: { label: 'Problème', bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle },
  };
  const status = statusConfig[commande.status] || statusConfig.processing;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer active:scale-[0.99]"
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          {commande.image ? (
            <img src={commande.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={20} className="text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{commande.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Vendu par {commande.seller}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 flex-shrink-0 ${status.bg} ${status.text}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Prix</p>
              <p className="text-sm font-bold text-[#1E3A5F]">{commande.price} €</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date</p>
              <p className="text-xs font-medium text-gray-700">{commande.date}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">N°</p>
              <p className="text-xs font-medium text-gray-700">{commande.id.split('-').pop()}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function CommandesMarketplace({ onSelectTool }) {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  // Popup principal
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  
  // Modals secondaires
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  
  // Review form
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);

  const [commandes, setCommandes] = useState([
    { id: 'CMD-2024-001', title: 'Perceuse Bosch Professional 18V', seller: 'BricoTools Pro', sellerType: 'pro', available: true, price: 189, status: 'shipping', date: '15 mai 2026', image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200', rated: false },
    { id: 'CMD-2024-002', title: 'Lot de vis inox 500pcs', seller: 'QuincaillerieExpress', sellerType: 'pro', available: true, price: 24, status: 'delivered', date: '10 mai 2026', image: null, rated: true },
    { id: 'CMD-2024-003', title: 'Niveau laser Bosch GLL 3-80', seller: 'OutillagePro', sellerType: 'pro', available: true, price: 299, status: 'processing', date: '18 mai 2026', image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=200', rated: false },
    { id: 'CMD-2024-004', title: 'Scie circulaire Makita', seller: 'BricoTools Pro', sellerType: 'pro', available: true, price: 245, status: 'delivered', date: '1 mai 2026', image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200', rated: false },
  ]);
  
  // Contacter le vendeur → Ouvrir le modal Ticket
  const handleContactSeller = () => {
    setShowUnavailable(false);
    setShowTicketModal(true);
  };
  
  // Racheter → Vérifier disponibilité puis ajouter au panier
  const handleRacheter = () => {
    if (!selectedCommande?.available) {
      setShowUnavailable(true);
      setTimeout(() => {
        setShowUnavailable(false);
      }, 2500);
    } else {
      setShowCartSuccess(true);
      setTimeout(() => {
        setShowCartSuccess(false);
        setSelectedCommande(null);
      }, 2000);
    }
  };
  
  // Ouvrir le modal d'avis
  const handleOpenReview = () => {
    setReviewRating(0);
    setReviewComment('');
    setShowReviewModal(true);
  };
  
  const handleSubmitReview = async () => {
    if (reviewRating === 0 || !reviewComment.trim()) return;
    
    // Marquer comme noté
    setCommandes(prev => prev.map(c => 
      c.id === selectedCommande.id ? { ...c, rated: true } : c
    ));
    setShowReviewModal(false);
    setSelectedCommande(null);
  };

  const filters = [
    { id: 'all', label: 'Toutes', icon: null },
    { id: 'processing', label: 'En préparation', icon: Clock },
    { id: 'shipping', label: 'En livraison', icon: Truck },
    { id: 'delivered', label: 'Livrées', icon: CheckCircle },
  ];
  
  // Compteurs par statut
  const filterCounts = {
    all: commandes.length,
    processing: commandes.filter(c => c.status === 'processing').length,
    shipping: commandes.filter(c => c.status === 'shipping').length,
    delivered: commandes.filter(c => c.status === 'delivered').length,
  };

  const filteredCommandes = commandes.filter(c => 
    (filter === 'all' || c.status === filter) &&
    (searchQuery === '' || c.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        {onSelectTool && (
          <button 
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Retour au tableau de bord"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Commandes</h1>
          <p className="text-xs sm:text-sm text-gray-500">{commandes.length} commande{commandes.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Recherche et Filtres - Style premium */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        {/* Recherche */}
        <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 border border-gray-200 rounded-lg">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Rechercher une commande..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
          />
        </div>

        {/* Filtres Mobile - Style premium avec scroll horizontal */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filters.map((f) => {
                const count = filterCounts[f.id] || 0;
                const FilterIcon = f.icon;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    data-testid={`filter-${f.id}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      filter === f.id
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {FilterIcon && <FilterIcon size={12} />}
                    {f.label}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      filter === f.id ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Filtres Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {filters.map((f) => {
            const count = filterCounts[f.id] || 0;
            const FilterIcon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === f.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {FilterIcon && <FilterIcon size={14} />}
                {f.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  filter === f.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      {filteredCommandes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune commande</h3>
          <p className="text-gray-500 mb-4">Tu n'as pas encore passé de commande</p>
          <Link to="/produits">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Découvrir la marketplace
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCommandes.map(commande => (
            <CommandeCard 
              key={commande.id} 
              commande={commande}
              onClick={() => setSelectedCommande(commande)}
            />
          ))}
        </div>
      )}
      
      {/* Popup Principal - Détails de la commande */}
      <AnimatePresence>
        {selectedCommande && !showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setSelectedCommande(null); setShowUnavailable(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              {showUnavailable ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Article plus disponible</h3>
                  <p className="text-sm text-gray-500 mb-4">Cet article a déjà été vendu et n'est plus disponible à l'achat.</p>
                  <Button
                    onClick={handleContactSeller}
                    variant="outline"
                    className="mx-auto"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Contacter le vendeur
                  </Button>
                </div>
              ) : showCartSuccess ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ajouté au panier !</h3>
                  <p className="text-sm text-gray-500">{selectedCommande?.title} a été ajouté à ton panier.</p>
                </div>
              ) : (
                <>
                  {/* Header avec image */}
                  <div className="relative">
                    <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                      {selectedCommande.image ? (
                        <img 
                          src={selectedCommande.image} 
                          alt={selectedCommande.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedCommande(null)}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <X size={18} className="text-gray-600" />
                    </button>
                    {/* Badge statut */}
                    <div className="absolute bottom-3 left-3">
                      {(() => {
                        const statusConfig = {
                          delivered: { label: 'Livré', bgColor: 'bg-green-500', icon: CheckCircle },
                          shipping: { label: 'En livraison', bgColor: 'bg-blue-500', icon: Truck },
                          processing: { label: 'En préparation', bgColor: 'bg-amber-500', icon: Clock },
                        };
                        const status = statusConfig[selectedCommande.status] || statusConfig.processing;
                        const StatusIcon = status.icon;
                        return (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white flex items-center gap-1.5 ${status.bgColor}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedCommande.title}</h2>
                    
                    {/* Prix */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl font-bold text-[#1E3A5F]">{selectedCommande.price} €</span>
                    </div>

                    {/* Infos vendeur */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vendu par</p>
                          <p className="font-medium text-gray-900">{selectedCommande.seller}</p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-xs text-gray-500">Commande</p>
                          <p className="font-medium text-gray-900 text-sm">{selectedCommande.id}</p>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                      <Calendar size={14} />
                      <span>Commandé le {selectedCommande.date}</span>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        onClick={handleContactSeller}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} />
                        Contacter le vendeur
                      </Button>
                      
                      {selectedCommande.status === 'delivered' && !selectedCommande.rated && (
                        <Button
                          onClick={handleOpenReview}
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white flex items-center justify-center gap-2"
                        >
                          <Star size={16} />
                          Laisser un avis
                        </Button>
                      )}
                      
                      <Button
                        onClick={handleRacheter}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Racheter cet article
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal Laisser un avis */}
      <AnimatePresence>
        {showReviewModal && selectedCommande && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Laisser un avis</h3>
                <button onClick={() => setShowReviewModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-14 h-14 bg-gray-200 rounded-lg overflow-hidden">
                    {selectedCommande.image ? (
                      <img src={selectedCommande.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={20} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{selectedCommande.title}</p>
                    <p className="text-xs text-gray-500">{selectedCommande.seller}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ta note</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setReviewRating(star)}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          size={32}
                          className={`${
                            star <= (hoverRating || reviewRating)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'
                          } transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ton avis</p>
                  <textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Partage ton expérience..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button variant="outline" className="flex-1" onClick={() => setShowReviewModal(false)}>
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                  onClick={handleSubmitReview}
                  disabled={reviewRating === 0 || !reviewComment.trim()}
                >
                  Publier mon avis
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Ticket (composant réutilisable) */}
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedCommande(null); }}
        recipientName={selectedCommande?.seller}
        recipientId={selectedCommande?.id}
        type="buyer"
        onSuccess={() => setSelectedCommande(null)}
      />
    </div>
  );
}
